import { useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PortfolioLayout } from '../Portfolio/PortfolioLayout';
import { CardHeading, CardInstructions } from 'features/loanTape/styles';
import FileUpload from '../DocumentLibrary/FileUpload/FileUpload';
import { FileType } from '__generated__/globalTypes';
import { uploadConfig } from 'app-level/config/uploadConfig';
import { GetUserCompany } from 'query/__generated__/GetUserCompany';
import { GET_USER_COMPANY } from 'query/company';
import { useQuery } from '@apollo/client';
import { Tab, Tabs } from 'ui-kit';
import { RemittanceUploadHistory } from './RemittanceUploadHistory';
import { RemittanceTapeUploadDialog, RemittanceUploadParams } from './RemittanceTapeUploadDialog';
import { ServicedDealsTable } from './ServicedDealsTable';
import { useDialog } from 'ui-kit';
import {
  useGetRemittanceTapePostUrl,
  uploadRemittanceTapeWithPostUrl,
} from './getRemittanceTapePostUrl_gql';

const REMITTANCE_BASE = '/portfolio/remittance';
const TAB_SERVICED = 'serviced-deals';
const TAB_UPLOAD = 'upload-history';

export type InProgressRemittanceUpload = {
  uploadId: string;
  dealId: string;
  paymentMonth: string;
  fileName: string;
  progress: number;
};

export const RemittanceHomePage = () => {
  const { data } = useQuery<GetUserCompany>(GET_USER_COMPANY);
  const uploadDialog = useDialog();
  const fileRef = useRef<File | null>(null);
  const [getRemittanceTapePostUrl] = useGetRemittanceTapePostUrl();
  const [inProgressUploads, setInProgressUploads] = useState<InProgressRemittanceUpload[]>([]);
  const location = useLocation();
  const navigate = useNavigate();

  const company = data?.user?.company ?? null;

  const activeTab = location.pathname.endsWith(`/${TAB_UPLOAD}`) ? 1 : 0;
  const handleTabChange = (index: number) => {
    const path =
      index === 0 ? `${REMITTANCE_BASE}/${TAB_SERVICED}` : `${REMITTANCE_BASE}/${TAB_UPLOAD}`;
    navigate(path);
  };

  const handleFileDropSuccess = (
    file: File,
    _uploadFile: (file: File) => Promise<string | undefined>,
  ) => {
    fileRef.current = file;
    uploadDialog.openDialog();
  };

  const handleOptimisticReplaced = useCallback((uploadIds: string[]) => {
    setInProgressUploads((prev) => prev.filter((u) => !uploadIds.includes(u.uploadId)));
  }, []);

  const handleRemittanceUpload = useCallback(
    async (file: File, params: RemittanceUploadParams) => {
      const mutate = (opts: Parameters<typeof getRemittanceTapePostUrl>[0]) =>
        getRemittanceTapePostUrl(opts).then((r) => ({ data: r.data ?? undefined }));

      let currentUploadId: string | null = null;

      try {
        await uploadRemittanceTapeWithPostUrl(mutate, file, params.dealId, params.paymentMonth, {
          onUploadId(uploadId) {
            currentUploadId = uploadId;
            setInProgressUploads((prev) => [
              {
                uploadId,
                dealId: params.dealId,
                paymentMonth: params.paymentMonth,
                fileName: file.name,
                progress: 0,
              },
              ...prev,
            ]);
          },
          onProgress(percent) {
            setInProgressUploads((prev) =>
              currentUploadId
                ? prev.map((u) =>
                    u.uploadId === currentUploadId ? { ...u, progress: percent } : u,
                  )
                : prev,
            );
          },
        });

        if (currentUploadId) {
          setInProgressUploads((prev) =>
            prev.map((u) => (u.uploadId === currentUploadId ? { ...u, progress: 100 } : u)),
          );
        }
      } catch (err) {
        if (currentUploadId) {
          setInProgressUploads((prev) => prev.filter((u) => u.uploadId !== currentUploadId));
        }
        throw err;
      }
      // Dialog closes on success via handleStartUpload in RemittanceTapeUploadDialog; on error
      // the promise rejects and the dialog stays open to show setUploadError.
    },
    [getRemittanceTapePostUrl],
  );

  return (
    <PortfolioLayout hideSummery={true}>
      <Tabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        tabListClassName="pb-4"
        tabPanelClassName="mt-4"
      >
        <Tab label="Serviced deals">
          <CardHeading>Serviced deals</CardHeading>
          <CardInstructions>
            Deals you are servicing or have purchased. Open a deal to view the latest month of
            loans.
          </CardInstructions>
          <ServicedDealsTable />
        </Tab>
        <Tab label="Upload & history">
          <CardHeading>Upload Your Loan Tape for Remittances</CardHeading>
          <CardInstructions>
            Upload one or all of your loan tapes to add to your portfolio
          </CardInstructions>
          <FileUpload
            parentId={data?.user.company.id || ''}
            fileType={FileType.OTHER}
            allowedFileTypes={uploadConfig.dealDocuments.allowedFileTypes || []}
            width="100%"
            showButtons={false}
            showProgress={false}
            onFileDropSuccess={handleFileDropSuccess}
          />
          <CardHeading style={{ marginTop: '2rem' }}>Previous Remittance Tapes</CardHeading>
          <RemittanceUploadHistory
            inProgressUploads={inProgressUploads}
            company={company ? { id: company.id, name: company.name } : undefined}
            onOptimisticReplaced={handleOptimisticReplaced}
          />
        </Tab>
      </Tabs>
      <RemittanceTapeUploadDialog
        dialog={uploadDialog}
        uploadfile={handleRemittanceUpload}
        file={fileRef.current}
      />
    </PortfolioLayout>
  );
};
